/**
 * Entry point — wires AudioEngine + Renderer + UI and starts the render loop.
 */

import "./ui/styles.css";

import { AudioEngine } from "./audio/sources";
import { ZERO_FEATURES, type AudioFeatures } from "./audio/types";
import { Renderer } from "./viz/renderer";
import { BackgroundRenderer } from "./viz/background";
import { initUI } from "./ui/controls";
import {
  isKnownSceneType,
  loadRunningState,
  readPresetFromLocation,
  saveRunningState,
} from "./presets/store";
import { disablePwaForRemoteRuntime, registerPwa } from "./pwa";
import { SCENES } from "./viz/scenes/scenePresets";
import { applyScenePreset } from "./viz/scenes/applyScenePreset";
import { resolveSceneLink } from "./viz/scenes/sceneLinks";
import { filterBySceneProfile } from "./ui/advancedControls";
import { RemoteRuntimeBridge, isRemoteRuntime } from "./remote/runtimeBridge";

type SceneAuditFixture = "quiet" | "bass" | "mid" | "treble" | "beat" | "tension";

function sceneAuditFeatures(fixture: SceneAuditFixture): AudioFeatures {
  const spectrum = new Float32Array(96);
  const waveform = new Float32Array(256);
  for (let index = 0; index < spectrum.length; index++) {
    const phase = index / Math.max(1, spectrum.length - 1);
    spectrum[index] = fixture === "quiet"
      ? 0.04 + Math.sin(phase * Math.PI * 6) * 0.015
      : fixture === "beat"
        ? Math.max(0.08, 0.9 * Math.exp(-phase * 4) + Math.sin(phase * 28) * 0.08)
        : 0.28 + Math.sin(phase * 35) * 0.18 + phase * 0.22;
  }
  for (let index = 0; index < waveform.length; index++) {
    const phase = index / waveform.length * Math.PI * 2;
    const amplitude = fixture === "quiet" ? 0.08 : fixture === "beat" ? 0.85 : 0.48;
    waveform[index] = Math.sin(phase * 3) * amplitude;
  }
  if (fixture === "beat") {
    return { bass: 0.95, mid: 0.42, treble: 0.28, level: 0.72, beat: 1, bpm: 128, beatCount: 16, t: performance.now(), spectrum, waveform };
  }
  if (fixture === "bass") {
    return { bass: 0.9, mid: 0.04, treble: 0.02, level: 0.32, beat: 0, bpm: 120, beatCount: 0, t: performance.now(), spectrum, waveform };
  }
  if (fixture === "mid") {
    return { bass: 0.03, mid: 0.9, treble: 0.04, level: 0.32, beat: 0, bpm: 120, beatCount: 0, t: performance.now(), spectrum, waveform };
  }
  if (fixture === "treble") {
    return { bass: 0.02, mid: 0.04, treble: 0.9, level: 0.32, beat: 0, bpm: 120, beatCount: 0, t: performance.now(), spectrum, waveform };
  }
  if (fixture === "tension") {
    return { bass: 0.44, mid: 0.78, treble: 0.72, level: 0.66, beat: 0.18, bpm: 116, beatCount: 8, t: performance.now(), spectrum, waveform };
  }
  return { bass: 0.05, mid: 0.08, treble: 0.04, level: 0.06, beat: 0, bpm: 74, beatCount: 0, t: performance.now(), spectrum, waveform };
}

function bootstrap(): void {
  const canvas = document.getElementById("viz");
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error("Missing #viz canvas element.");
  }

  const params = new URLSearchParams(window.location.search);
  const remoteEnabled = isRemoteRuntime(params);
  const engine = remoteEnabled ? null : new AudioEngine();
  const renderer = new Renderer(canvas);
  const sceneAuditEnabled = params.get("sceneAudit") === "1";
  let auditFixture: SceneAuditFixture = "quiet";
  let auditSceneName: string | null = null;
  let auditLiveFeatures: AudioFeatures | null = null;

  // Restore the last-known visual params *before* the UI builds, so every
  // slider initialises on the saved value instead of a default.
  if (!remoteEnabled && !sceneAuditEnabled) {
    const runningState = loadRunningState();
    Object.assign(renderer.params, runningState.params);
    renderer.sceneType = runningState.sceneType;
  }

  // Shared deep-link (?preset=<base64>) overrides the auto-saved params so
  // recipients of a share URL see the sender's exact look — vizz.fm-style.
  // We do this *after* loadRunningParams so the link wins, but *before* the
  // UI builds so every slider mounts on the shared values.
  const shared = remoteEnabled ? null : readPresetFromLocation();
  if (shared) {
    Object.assign(renderer.params, shared.params);
    renderer.sceneType = shared.sceneType;
  }

  const sceneParam = params.get("scene");
  const sceneLink = remoteEnabled ? null : resolveSceneLink(sceneParam);
  if (sceneLink) {
    applyScenePreset(renderer, sceneLink.scene);
  } else if (!remoteEnabled && isKnownSceneType(sceneParam)) {
    renderer.sceneType = sceneParam;
  }

  const remoteBridge = remoteEnabled
    ? new RemoteRuntimeBridge(renderer, canvas, params)
    : null;

  // Auto-save the running params at most once per second. The renderer mutates
  // params in-place from the UI, so we just snapshot whatever's there. If the
  // user hasn't touched anything, the write is a no-op-equivalent (same JSON).
  const PARAM_SAVE_INTERVAL_MS = 1000;
  // Audit runs are hermetic: several can be open at once and none of them
  // should overwrite the operator's saved look.
  const saveInterval = remoteEnabled || sceneAuditEnabled
    ? null
    : window.setInterval(() => {
      saveRunningState(renderer.sceneType, renderer.params);
    }, PARAM_SAVE_INTERVAL_MS);

  const backdropEl = document.getElementById("backdrop");
  // BackgroundRenderer manages 6 styles (tint / solid / linear / radial /
  // starfield / plasma). With style="tint" it preserves the original
  // renderer-driven backdrop colour exactly.
  const background = backdropEl ? new BackgroundRenderer(backdropEl) : null;
  const hudBpm = document.getElementById("hudBpm");
  const hudBass = document.getElementById("hudBass");
  const hudMid = document.getElementById("hudMid");
  const hudTreble = document.getElementById("hudTreble");

  // Renderer reads features from the engine on every frame, and we mirror the
  // scene's backdrop tint + a few feature numbers into the DOM so the HUD and
  // the atmospheric background pulse with the music alongside the canvas.
  let disposed = false;
  let hudRafId: number | null = null;
  let hudTimer: number | null = null;
  const scheduleTick = (): void => {
    if (disposed) return;
    if (document.hidden) hudTimer = window.setTimeout(tick, 1000 / 30);
    else hudRafId = requestAnimationFrame(tick);
  };
  const tick = (): void => {
    hudRafId = null;
    hudTimer = null;
    if (disposed) return;
    const f = sceneAuditEnabled
      ? auditLiveFeatures ?? sceneAuditFeatures(auditFixture)
      : remoteBridge?.read() ?? engine?.read() ?? ZERO_FEATURES;
    renderer.setFeatures(f);

    if (backdropEl) {
      const bd = renderer.backdrop;
      if (background) {
        background.tick(f, bd);
      } else {
        backdropEl.style.background = `radial-gradient(ellipse at 50% 45%, rgba(${bd.r}, ${bd.g}, ${bd.b}, ${bd.alpha.toFixed(3)}) 0%, #010104 70%, #000 100%)`;
        backdropEl.style.opacity = String(Math.min(1, bd.alpha + 0.25));
        backdropEl.style.filter = `blur(${bd.blurPx}px)`;
      }
    }

    if (hudBpm) {
      hudBpm.textContent = f.bpm > 0 ? Math.round(f.bpm).toString() : "---";
      hudBpm.classList.toggle("beat", f.beat > 0.4);
    }
    if (hudBass) hudBass.style.setProperty("--lvl", `${Math.round(f.bass * 100)}%`);
    if (hudMid) hudMid.style.setProperty("--lvl", `${Math.round(f.mid * 100)}%`);
    if (hudTreble)
      hudTreble.style.setProperty("--lvl", `${Math.round(f.treble * 100)}%`);

    scheduleTick();
  };
  scheduleTick();

  renderer.start();
  if (engine) initUI({ engine, renderer, background });

  window.addEventListener("pagehide", (event: PageTransitionEvent) => {
    if (!remoteEnabled) saveRunningState(renderer.sceneType, renderer.params);
    if (event.persisted || disposed) return;
    disposed = true;
    if (saveInterval !== null) window.clearInterval(saveInterval);
    if (hudRafId !== null) cancelAnimationFrame(hudRafId);
    if (hudTimer !== null) window.clearTimeout(hudTimer);
    remoteBridge?.stop();
    renderer.dispose();
    engine?.dispose();
  });

  if (sceneAuditEnabled) {
    const auditWindow = window as typeof window & {
      __pulsefxSceneAudit?: {
        listScenes(): Array<{ name: string; sceneType: string }>;
        selectScene(name: string): boolean;
        setFixture(fixture: SceneAuditFixture): void;
        setFeatures(features: Partial<AudioFeatures> | null): void;
        listControls(): Array<{ key: string; kind: string; min?: number; max?: number }>;
        setParam(key: string, value: number | boolean | string): boolean;
        pause(): void;
        resume(): void;
        renderFrame(): void;
        getState(): {
          sceneName: string | null;
          sceneType: string;
          frameMs: number;
          bioclast: ReturnType<Renderer["getBioclastDiagnostics"]>;
          renderer: ReturnType<Renderer["getDiagnostics"]>;
        };
      };
    };
    auditWindow.__pulsefxSceneAudit = {
      listScenes: () => SCENES.map((scene) => ({
        name: scene.name,
        sceneType: scene.sceneType ?? "bioclast",
      })),
      selectScene: (name) => {
        const scene = SCENES.find((candidate) => candidate.name === name);
        if (!scene) return false;
        renderer.setCameraControlMode("auto");
        applyScenePreset(renderer, scene);
        auditSceneName = scene.name;
        return true;
      },
      setFixture: (fixture) => {
        auditFixture = fixture;
      },
      // Live override for same-origin hosts driving audit tiles from a real
      // analyser. Passing null falls back to the synthetic fixtures.
      setFeatures: (features) => {
        if (!features) {
          auditLiveFeatures = null;
          return;
        }
        const unit = (value: unknown, fallback: number): number => {
          const parsed = Number(value);
          return Math.min(1, Math.max(0, Number.isFinite(parsed) ? parsed : fallback));
        };
        const bass = unit(features.bass, 0);
        const mid = unit(features.mid, 0);
        const treble = unit(features.treble, 0);
        auditLiveFeatures = {
          bass,
          mid,
          treble,
          level: unit(features.level, Math.max(bass, mid, treble)),
          beat: unit(features.beat, 0),
          beatCount: Math.max(0, Math.round(Number(features.beatCount) || 0)),
          bpm: Math.max(0, Number(features.bpm) || 0),
          t: performance.now(),
        };
      },
      listControls: () => {
        const scene = SCENES.find((candidate) => candidate.name === auditSceneName);
        if (!scene) return [];
        return filterBySceneProfile({
          sceneName: scene.name,
          sceneType: scene.sceneType ?? "bioclast",
          profile: scene.controlProfile,
          values: scene.values,
        }).flatMap((section) => section.controls.map((control) => ({
          key: control.key,
          kind: control.kind,
          ...(control.kind === "slider" ? { min: control.min, max: control.max } : {}),
        })));
      },
      setParam: (key, value) => {
        if (!(key in renderer.params)) return false;
        (renderer.params as unknown as Record<string, unknown>)[key] = value;
        return true;
      },
      pause: () => renderer.stop(),
      resume: () => renderer.start(),
      renderFrame: () => {
        (renderer as unknown as { draw(): void }).draw();
      },
      getState: () => ({
        sceneName: auditSceneName,
        sceneType: renderer.sceneType,
        frameMs: renderer.frameMs,
        bioclast: renderer.getBioclastDiagnostics(),
        renderer: renderer.getDiagnostics(),
      }),
    };
  }

  // Optional ?ui=hidden or ?ui=0 URL flag — handy for clean wall/OBS output.
  if (remoteEnabled || params.get("ui") === "hidden" || params.get("ui") === "0") {
    document.body.classList.add("ui-hidden");
  }

  // Surface unhandled errors in the source-status line, never silently fail.
  window.addEventListener("error", (ev) => {
    console.error(ev.error ?? ev.message);
  });
  window.addEventListener("unhandledrejection", (ev) => {
    console.error(ev.reason);
  });
}

async function launch(): Promise<void> {
  const remoteEnabled = isRemoteRuntime(new URLSearchParams(window.location.search));
  if (remoteEnabled) await disablePwaForRemoteRuntime();
  bootstrap();
  if (!remoteEnabled) registerPwa();
}

void launch();
